import React, {useCallback, useContext, useMemo, useState} from 'react';
import {jsonToCSV} from 'react-papaparse';

import moment from 'moment/moment';
import {
    AddedFile,
} from 'src/modules/create-user/user-bulk-import/steps/upload-file-step/user-import-file-upload/added-files';
import {DropZoneView} from 'src/modules/create-user/user-bulk-import/steps/upload-file-step/user-import-file-upload/drop-zone-view';
import {getLoginLinksByEmails} from 'src/openapi/admin/apis/LoginLinkControllerApi';
import {useAppDispatch} from 'src/store/hooks';
import {mapDateFormat} from 'src/utils/mapDateFormat';

import Button, {FlatButton} from '@pb/kit-component-button';
import {DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {DropZone} from '@pb/kit-component-dropzone';
import {Group} from '@pb/kit-component-group';
import Icon, {Icons, Sizes} from '@pb/kit-component-icon';
import {Message} from '@pb/kit-component-message';
import {LocaleContext} from '@pb/kit-context-locale';

import {MATCHING_LIST, MAX_RECORD_SIZE, UPLOAD_LOGIN_LINK_GENERATOR_TEMPLATE} from './constants';
import {parseLoginLinkGeneratorFile, validateLoginLinkGeneratorFile} from './login-link-generator-tool-file-parser';

import {
    uploadFileBlockAside,
    uploadFileBlockContent,
    uploadFileBlockDropzone,
    uploadFileBlockHeader,
    uploadFileBlockList,
    uploadFileBlockReviewPartition,
    uploadFileBlockUploadPartition,
} from './login-link-generator-tool.module.styl';

import type {GetLoginLinksResponse} from 'src/openapi/admin/models/GetLoginLinksResponse';

type Props = {
    onCloseModal: (success: boolean) => void,
};

export const LoginLinkGeneratorToolModal = ({onCloseModal}: Props) => {
    const apiDispatch = useAppDispatch();
    const {locale} = useContext(LocaleContext);

    const [addedFile, setAddedFile] = useState<File | null>(null);
    const [uploadFileError, setUploadFileError] = useState<string>('');
    const [submitBusy, setSubmitBusy] = useState(false);
    const [userLink, setUserLink] = useState(Array<GetLoginLinksResponse>);

    const onDownloadTemplateHandler = () => {
        window.open(UPLOAD_LOGIN_LINK_GENERATOR_TEMPLATE);
    };

    const buildResultFile = () => {
        const customHeaders = [{
            id: 'User ID',
            email: 'Email',
            firstName: 'First Name',
            lastName: 'Last Name',
            loginLink: 'Login Link',
            error: 'Error',
        }];

        const fileName = 'Generated-login-links';
        const dataWithHeaders = [...customHeaders, ...userLink];
        const csv = jsonToCSV(dataWithHeaders, {
            delimiter: ',',
            header: false,
            newline: '\r\n',
            quotes: true,
            skipEmptyLines: true,
        });

        const blob = new Blob([csv], {type: 'text/csv'});
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        link.download = `${fileName}_${moment().format(
            mapDateFormat(locale.dateFormat, 'FILE_NAME', 'DATE_TIME'),
        )}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const onDrop = useCallback(async (accepted: Array<File>) => {
        let loadedFile = null;
        setSubmitBusy(true);
        try {
            await validateLoginLinkGeneratorFile(accepted[0]);
            loadedFile = accepted[0];
            setUploadFileError('');
        } catch (
            /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
            error: any
        ) {
            setUploadFileError(error.message);
            setSubmitBusy(false);
        }
        setAddedFile(loadedFile);
        if (loadedFile) {
            parseLoginLinkGeneratorFile(loadedFile).then(parsedData => {
                apiDispatch(getLoginLinksByEmails({getLoginLinksByEmailsRequest: {emails: parsedData}}))
                    .then(response => {
                        if (response.payload.success) {
                            setUserLink(response.payload.data);
                        } else {
                            setUploadFileError('Failed to generate login links. Please try again.');
                        }
                        setSubmitBusy(false);
                    });
            }, error => {
                if (error) {
                    setUploadFileError('File can\'t be parsed. Please make sure the file matches the template.');
                    setSubmitBusy(false);
                }
            });
        }
    }, [setUploadFileError, apiDispatch]);

    const addedFilesView = useMemo(() => {
        return (addedFile && !uploadFileError
            ? <AddedFile addedFile={addedFile} removeFile={() => setAddedFile(null)}/>
            : null
        );
    }, [addedFile, uploadFileError]);

    const onSubmit = () => {
        onCloseModal(true);
        buildResultFile();
    };

    return (
        <>
            <DialogHeader onClose={() => onCloseModal(false)}>
                Login Link Generator
            </DialogHeader>
            <DialogMain style={{padding: 24}}>
                <Group className={uploadFileBlockContent} direction="v">
                    <div>
                        Upload a list of the users. <b>Please note:</b> Email is the only required field.
                        Download the <FlatButton onClick={() => onDownloadTemplateHandler()}>template</FlatButton> to
                        get started.
                    </div>
                    <div className={uploadFileBlockReviewPartition}>
                        <Group gap="l" alignItems="start">
                            <aside className={uploadFileBlockAside}>
                                <header className={uploadFileBlockHeader}>
                                    <b>Matching Criteria:</b>
                                </header>
                                <ul className={uploadFileBlockList}>
                                    {MATCHING_LIST.map(({caption, required}, index) => (
                                        <li key={caption}>
                                            {`${index + 1}.`}&nbsp;&nbsp;
                                            {required ? <b>{caption}</b> : caption}
                                        </li>
                                    ))}
                                </ul>
                            </aside>
                            <section className={uploadFileBlockUploadPartition}>
                                <DropZone multiple={false} onDrop={onDrop} mix={uploadFileBlockDropzone}>
                                    <DropZoneView addedFile={addedFile} maxRecords={MAX_RECORD_SIZE - 1}/>
                                </DropZone>
                                {addedFilesView}
                                {uploadFileError ? (
                                    <Message color="error" iconProps={{type: Icons.ERROR, size: Sizes.Size_14}}>
                                        {uploadFileError}
                                    </Message>
                                ) : null}
                            </section>
                        </Group>
                    </div>
                </Group>
            </DialogMain>
            <DialogFooter>
                <Group>
                    <Button
                        variant="Type.PRIMARY"
                        startIcon={<Icon type={Icons.DOWNLOAD}/>}
                        onClick={onSubmit}
                        disabled={!addedFile}
                        busy={submitBusy}>
                        Download Login Links
                    </Button>
                    <Button variant="Type.SECONDARY" onClick={() => onCloseModal(false)}>Cancel</Button>
                </Group>
            </DialogFooter>
        </>
    );
};
